'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

export const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY
export const TURNSTILE_SCRIPT =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

type TurnstileApi = {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string
      callback: (token: string) => void
      'error-callback'?: () => void
      'expired-callback'?: () => void
      appearance?: 'always' | 'execute' | 'interaction-only'
      theme?: 'light' | 'dark' | 'auto'
    }
  ) => string
  reset: (id?: string) => void
}

declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

/**
 * The Turnstile widget as a hook: renders invisibly for most people and only
 * shows a challenge when Cloudflare is unsure. The token travels with the
 * create request and is verified server-side before anything is spent. With
 * no site key configured, `ready` is true immediately and `token` stays null.
 */
export function useTurnstile() {
  const [token, setToken] = useState<string | null>(null)
  const [ready, setReady] = useState(!TURNSTILE_SITE_KEY)
  const widgetRef = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | null>(null)

  const render = useCallback(() => {
    if (!TURNSTILE_SITE_KEY || !widgetRef.current || !window.turnstile || widgetId.current) return
    widgetId.current = window.turnstile.render(widgetRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      appearance: 'interaction-only',
      theme: 'auto',
      callback: (t) => {
        setToken(t)
        setReady(true)
      },
      'expired-callback': () => setToken(null),
      'error-callback': () => {
        setToken(null)
        setReady(true)
      },
    })
  }, [])

  useEffect(() => {
    if (window.turnstile) render()
  }, [render])

  const reset = useCallback(() => {
    setToken(null)
    if (widgetId.current && window.turnstile) window.turnstile.reset(widgetId.current)
  }, [])

  return { token, ready, widgetRef, render, reset, enabled: Boolean(TURNSTILE_SITE_KEY) }
}
